import { type PayloadAction } from '@reduxjs/toolkit';
import { maxPaletteSize } from '@common/constants/colorPalette';
import { TargetTab } from '@common/store';
import { type SetColorsProps } from '@common/store/actions/types';
import { type ColorsStoreState, type GraphsStoreState } from '@common/store/reducers/graphs/types';
import { type RegisterDataSeries } from '@common/types/models/SelectedDataPoints';
import { type BitMask16 } from '@common/utils/formatting/multiOptions';

export const compositeKey = (item: RegisterDataSeries) => `${item.id}-${item.valueBitMask}`;

export const colorMappingKey = (
  registerIndex: string | undefined,
  secondId: number | undefined,
) => {
  const suffix = secondId ? `:${secondId}` : '';
  return registerIndex + suffix;
};

export const getPaletteIndex = (
  registerIndex: string,
  valueBitMask: BitMask16 | undefined,
  state: ColorsStoreState,
) => {
  const { colorsInUse, colorMapping } = state;
  const key = colorMappingKey(registerIndex, valueBitMask);

  if (colorsInUse[key] != null) {
    return colorsInUse[key];
  }

  let paletteIndex = colorMapping[key];
  if (paletteIndex == null) {
    // Choose least used palette entry
    const counts: number[] = new Array(maxPaletteSize).fill(0);
    Object.values(colorsInUse).forEach((i) => {
      counts[i] += 1;
    });
    paletteIndex = counts.indexOf(Math.min(...counts));
  }

  state.colorsInUse[key] = paletteIndex;
  return paletteIndex;
};

export const setColorsReducer = (
  state: GraphsStoreState,
  action: PayloadAction<SetColorsProps>,
) => {
  const { colorPalette, colorMapping } = action.payload;
  state.shared.colorPalette = colorPalette;
  state.shared.colorMapping = colorMapping;
  state.shared.colorsInUse = {};

  const updateColorIndexForSeries = (registerDataSeries: RegisterDataSeries) => {
    const { registerIndex, valueBitMask } = registerDataSeries;
    const key = colorMappingKey(registerIndex, valueBitMask);
    if (colorMapping[key] != null) {
      registerDataSeries.colorIndex = colorMapping[key];
    }
  };

  // Update colorIndexes for datapoints that have a user specified mapping whenever colorMapping is changed.
  for (const targetTab of [TargetTab.Diagram, TargetTab.Statistics]) {
    for (const graphs of Object.values(state[targetTab].selectedRegisterDataSeriesGraphs)) {
      for (const graph of graphs) {
        graph.registerDataSeriesArray.forEach(updateColorIndexForSeries);
      }
    }
  }
};
